import React, { SetStateAction, useState } from "react"
import {
	LineChart,
	Line,
	Tooltip,
	Legend,
	XAxis,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
} from "recharts"
import { useIntl, FormattedMessage } from "react-intl"
import { Chip } from "@walmart-web/livingdesign-components"
import ChartContainer from "components/Shared/ChartContainer"
import { formatNumber } from "helpers/numbers"
import { FormatProps, chipsAvailable, CHIP_TRANSLATIONS } from "./constants"
import { useFiltersContext } from "../GlobalFilter/context/filtersContext"
import { CustomerProfilingTimeline } from "components/CustomerBehavior/CustomerProfiling/service"
import "./index.scss"
import { timelineId } from "components/CustomerBehavior/CustomerProfiling/constants"

interface TimelineProps {
	data: CustomerProfilingTimeline
	format: FormatProps[]
	grid?: boolean
	isLoading?: boolean
	title: {
		id: string
		values?: Record<string, any>
	}
	subtitle?: {
		id: string
		values?: Record<string, any>
	}
	body?: React.ReactNode
	tooltip?: React.ReactElement
	interval: string
	setInterval: React.Dispatch<SetStateAction<string>>
}

interface ChipGroupProps {
	interval: string
	setInterval: React.Dispatch<SetStateAction<string>>
	period: string
}

const ChipGroup: React.FC<ChipGroupProps> = ({
	interval,
	setInterval,
	period,
}) => {
	const CHIPS = chipsAvailable(period)

	return (
		<div className="chip-group">
			{CHIP_TRANSLATIONS.map((item) => (
				<Chip
					key={`chip-${item.interval}`}
					selected={item.interval === interval}
					disabled={!CHIPS[item.interval]}
					onClick={() => setInterval(item.interval)}
				>
					<FormattedMessage id={item.id} />
				</Chip>
			))}
		</div>
	)
}

const Timeline: React.FC<TimelineProps> = ({
	data,
	format,
	grid,
	isLoading,
	title,
	subtitle,
	body,
	tooltip,
	interval,
	setInterval,
}) => {
	const intl = useIntl()
	const { period } = useFiltersContext()

	const dataKeys = format.map((item) => item.dataKey)

	const [hide, setHide] = useState<Record<string, boolean>>(
		Object.fromEntries(dataKeys.map((value) => [value, false]))
	)

	const onClickLegend = (object: any) => {
		setHide({ ...hide, [object.dataKey]: !hide[object.dataKey] })
	}

	return (
		<ChartContainer
			title={title}
			body={subtitle}
			className="timeline"
			isLoading={isLoading}
			buttons={
				<ChipGroup
					interval={interval}
					setInterval={setInterval}
					period={period}
				/>
			}
		>
			{body}
			<ResponsiveContainer width="100%" height={500}>
				<LineChart height={500} width={300} data={data.data}>
					<YAxis
						label={{
							value: intl.formatMessage({
								id: timelineId + data.metricName,
							}),
							angle: -90,
							position: "insideLeft",
						}}
						tickFormatter={(value) => formatNumber(value, intl)}
					/>
					<XAxis dataKey="dateNumber" />
					<Legend
						onClick={onClickLegend}
						formatter={(value) => (
							<FormattedMessage id={`phronesis.timeline.${value}`} />
						)}
					/>
					{grid && <CartesianGrid strokeDasharray="3 3" />}
					{tooltip && (
						<Tooltip content={(props) => React.cloneElement(tooltip, props)} />
					)}
					{format.map((item) => (
						<Line
							key={item.dataKey}
							type="monotone"
							hide={hide[item.dataKey]}
							strokeWidth={2}
							{...item}
						/>
					))}
				</LineChart>
			</ResponsiveContainer>
		</ChartContainer>
	)
}

export default Timeline
